import React, { useState } from "react";
import { IconButton } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import DescriptionIcon from "@mui/icons-material/Description";
import InspectionViewseer from "./AllRemarks";
import FormattedDate from "../../../components/TimeStamp";
import InspectionPhotoViewer from "../../../../../templates/ApplicationDetails/components/EmpReusableComp/DocumentViewSite";
import SafeHtml from "@egovernments/digit-ui-module-common/src/sharedComponents/SafeHtml";
import dayjs from "dayjs";

const DetailInfoTable = ({
  detailData,
  type = "paymentDetails",
  formData,
}) => {
  /**
   * Get the latest log for EACH officer.
   *
   * Important:
   * We do NOT pick one latest log from the complete array.
   * We group logs by officer and then pick the latest log
   * within each officer's group.
   */
  const getLatestLogPerOfficer = (data) => {
    if (!Array.isArray(data)) {
      return data ? [data] : [];
    }

    if (!data.length) {
      return [];
    }

    const latestByOfficer = {};

    data.forEach((item) => {
      const user = item?.userDetails || {};

      /**
       * Prefer a unique officer identifier.
       * uuid / employeeId / id are safer than employeeName.
       */
      const officerKey =
        user?.uuid ||
        user?.employeeId ||
        user?.id ||
        user?.employeeName ||
        user?.name ||
        "unknown";

      const currentTime = new Date(
        item?.auditDetails?.createdTime || 0
      ).getTime();

      const existingItem = latestByOfficer[officerKey];

      // First log for this officer
      if (!existingItem) {
        latestByOfficer[officerKey] = item;
        return;
      }

      const existingTime = new Date(
        existingItem?.auditDetails?.createdTime || 0
      ).getTime();

      // Replace only if current log is newer
      if (currentTime > existingTime) {
        latestByOfficer[officerKey] = item;
      }
    });

    return Object.values(latestByOfficer);
  };

  /**
   * One latest log per officer.
   */
  const detailsArray = getLatestLogPerOfficer(detailData);

  const [selectedDetails, setSelectedDetails] = useState([]);
  const [userDetails, setUserDetails] = useState(null);
  const [createdTime, setCreatedTime] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [showDocPopup, setShowDocPopup] = useState(false);
  const [docPopupData, setDocPopupData] = useState(null);

  const formatTime = (time) => {
    return time
      ? dayjs.utc(time).local().format("DD MMM YYYY, hh:mm A")
      : "N/A";
  };

  const openPopup = (item) => {
    let rows = [];

    if (type === "paymentDetails") {
      rows =
        item?.paymentDetails?.paymentHead?.[0]?.paymentHeads || [];
    } else if (type === "inspectionDetails") {
      rows = item?.inspectionDetails || {};
    } else if (type === "documentDetails") {
      rows = item?.documentDetails?.documents || [];
    }

    setSelectedDetails(rows);
    setUserDetails(item?.userDetails || {});
    setCreatedTime(item?.auditDetails?.createdTime || null);
    setPopupData(item);
    setShowPopup(true);

    console.log("Selected Item for Popup:", item);
  };

  const openPopupDocument = (item) => {
    if (type === "inspectionDetails") {
      setDocPopupData(item);
      setShowDocPopup(true);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedDetails([]);
    setUserDetails(null);
    setPopupData(null);
  };

  const printPopup = () => {
    const popupContent =
      document.getElementById("popup-print-content");

    if (!popupContent) return;

    const printWindow = window.open(
      "",
      "_blank",
      "width=800,height=600"
    );

    printWindow.document.write(`
      <html>
        <head>
          <title>Print Details</title>
          <style>
            body {
              font-size: 11px;
              font-family: Arial;
            }

            table {
              width: 100%;
              border-collapse: collapse;
            }

            th,
            td {
              border: 1px solid #ccc;
              padding: 6px;
            }

            th {
              background-color: #e2e8f0;
              font-size: 13px;
            }
          </style>
        </head>

        <body>
          ${popupContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div>
      <h2
        style={{
          fontSize: "16px",
          fontWeight: 600,
          marginBottom: "10px",
          backgroundColor:
            "linear-gradient(to right, #1E73BE, #E3F2FD)",
          padding: "5px",
        }}
      >
        {type === "paymentDetails"
          ? "Payment Remarks Summary"
          : type === "inspectionDetails"
            ? "Inspection Remarks Summary"
            : "Document Details Summary"}
      </h2>

      <div
        style={{
          overflowX: "auto",
          maxHeight: "300px",
          overflowY: "scroll",
        }}
      >
        <table
          border="1"
          cellPadding="8"
          width="100%"
          style={{
            borderCollapse: "collapse",
            minWidth: "800px",
            fontSize: "11px",
            position: "relative",
          }}
        >
          <thead
            style={{
              backgroundColor: "rgba(52, 73, 94, 0.94)",
              position: "sticky",
              top: 0,
              zIndex: 2,
            }}
          >
            <tr
              style={{
                fontSize: "14px",
                backgroundColor: "rgba(52, 73, 94, 0.94)",
                color: "#fff",
              }}
            >
              {type === "documentDetails" ? (
                <React.Fragment>
                  <th>User</th>
                  <th>Designation</th>
                  <th>Recommendation</th>
                  <th>Date & Time</th>
                  <th>Remarks</th>
                  <th>View</th>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <th>User</th>
                  <th>Designation</th>
                  <th>Recommendation</th>
                  <th>Date & Time</th>
                  <th>Details</th>

                  {type === "inspectionDetails" && (
                    <th>Documents</th>
                  )}

                  <th>Remarks</th>
                </React.Fragment>
              )}
            </tr>
          </thead>

          <tbody>
            {detailsArray.map((item, idx) => {
              const user = item?.userDetails || {};

              const remarks =
                type === "paymentDetails"
                  ? item?.paymentDetails?.developerRemarks
                  : type === "inspectionDetails"
                    ? item?.inspectionDetails
                      ?.inspectionReportData?.developerRemarks
                    : item?.documentDetails?.developerRemarks;

              const recommendation =
                type === "paymentDetails"
                  ? item?.paymentDetails?.dataRecommendation
                  : type === "inspectionDetails"
                    ? item?.inspectionDetails
                      ?.siteInspectionRecommendation
                    : item?.documentDetails?.dataRecommendation;

              return (
                <tr key={idx}>
                  {type === "documentDetails" ? (
                    <React.Fragment>
                      <td>
                        {user?.employeeName ||
                          user?.name ||
                          "-"}
                      </td>

                      <td>{user?.designation || "-"}</td>

                      <td>{recommendation || "-"}</td>

                      <td>
                        {formatTime(
                          item?.auditDetails?.createdTime
                        )}
                      </td>

                      <SafeHtml
                        html={remarks || "-"}
                        tag="td"
                      />

                      <td>
                        <IconButton
                          onClick={() => openPopup(item)}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </td>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <td>
                        {user?.employeeName ||
                          user?.name ||
                          "-"}
                      </td>

                      <td>{user?.designation || "-"}</td>

                      <td>{recommendation || "-"}</td>

                      <td>
                        {formatTime(
                          item?.auditDetails?.createdTime
                        )}
                      </td>

                      <td>
                        <IconButton
                          onClick={() => openPopup(item)}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </td>

                      {type === "inspectionDetails" && (
                        <td>
                          <IconButton
                            onClick={() =>
                              openPopupDocument(item)
                            }
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </td>
                      )}

                      <SafeHtml
                        html={remarks || "-"}
                        tag="td"
                      />
                    </React.Fragment>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Popup */}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: "15%",
            left: "60%",
            transform: "translateX(-50%)",
            backgroundColor: "#f3f4f6",
            border: "1px solid #ccc",
            padding: "20px",
            zIndex: 1000,
            maxHeight: "80vh",
            overflowY: "auto",
            width: "75%",
            boxShadow: "0 0 10px rgba(0,0,0,0.3)",
            fontSize: "11px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <h3 style={{ fontSize: "14px" }}>
              {type === "paymentDetails"
                ? "Payment Heads"
                : type === "inspectionDetails"
                  ? "Inspection Info"
                  : "Document Details"}
            </h3>

            <div>
              <IconButton
                onClick={printPopup}
                title="Print"
              >
                <PrintIcon />
              </IconButton>

              <IconButton
                onClick={closePopup}
                title="Close"
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>

          <div id="popup-print-content">
            {userDetails && (
              <div style={{ marginBottom: "10px" }}>
                <strong>User:</strong>{" "}
                {userDetails?.employeeName ||
                  userDetails?.name ||
                  "-"}{" "}
                |{" "}
                <strong>Designation:</strong>{" "}
                {userDetails?.designation || "-"}{" "}
                | <strong>Role:</strong>{" "}
                {userDetails?.role || "-"} |{" "}
                <strong>Created At:</strong>{" "}
                <FormattedDate timestamp={createdTime} />
              </div>
            )}

            {type === "inspectionDetails" ? (
              <InspectionViewseer
                inspectionDetails={selectedDetails}
                formData={formData}
              />
            ) : (
              <table
                border="1"
                cellPadding="6"
                width="100%"
                style={{
                  borderCollapse: "collapse",
                  fontSize: "11px",
                }}
              >
                <thead
                  style={{
                    backgroundColor: "#e2e8f0",
                  }}
                >
                  <tr>
                    {type === "documentDetails" ? (
                      <React.Fragment>
                        <th>Document Remarks</th>
                        <th>Document Status</th>
                        <th>Document Type</th>
                        <th>File Name</th>
                      </React.Fragment>
                    ) : (
                      <React.Fragment>
                        <th>Fee Type</th>
                        <th>Plot BUA</th>
                        <th>Rate</th>
                        <th>Amount</th>
                      </React.Fragment>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {selectedDetails?.map((row, i) => (
                    <tr key={i}>
                      {type === "paymentDetails" ? (
                        <React.Fragment>
                          <td>{row?.feeType}</td>
                          <td>
                            {row?.plotBuildUpArea}
                          </td>
                          <td>{row?.rate}</td>
                          <td>{row?.amount}</td>
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <td>
                            {row?.documentRemarks}
                          </td>

                          <td>
                            {row?.documentStatus}
                          </td>

                          <td>
                            {row?.documentType}
                          </td>

                          <td>
                            {row?.fileStoreUrl && (
                              <IconButton
                                href={row.fileStoreUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                style={{
                                  marginLeft: 8,
                                }}
                                title="Open Document"
                              >
                                <DescriptionIcon fontSize="small" />
                              </IconButton>
                            )}
                          </td>
                        </React.Fragment>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {type === "paymentDetails" &&
              popupData?.paymentDetails?.paymentHead?.[0]
                ?.estimateAmount && (
                <div
                  style={{
                    margin: "10px 0",
                    fontWeight: "bold",
                  }}
                >
                  Estimated Total Amount: ₹
                  {popupData.paymentDetails.paymentHead?.[0]?.estimateAmount.toLocaleString(
                    "en-IN"
                  )}
                </div>
              )}
          </div>
        </div>
      )}

      {/* Document Popup for Inspection */}
      {showDocPopup && (
        <div
          style={{
            position: "fixed",
            top: "15%",
            left: "60%",
            transform: "translateX(-50%)",
            backgroundColor: "#f3f4f6",
            border: "1px solid #ccc",
            padding: "20px",
            zIndex: 1000,
            maxHeight: "80vh",
            overflowY: "auto",
            width: "75%",
            boxShadow: "0 0 10px rgba(0,0,0,0.3)",
            fontSize: "11px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3 style={{ fontSize: "14px" }}>
              Document View
            </h3>

            <IconButton
              onClick={() => setShowDocPopup(false)}
              title="Close"
            >
              <CloseIcon />
            </IconButton>
          </div>

          {docPopupData?.inspectionDetails
            ?.inspectionReportData?.documentInspection
            ?.length > 0 && (
              <InspectionPhotoViewer
                documentInspection={
                  docPopupData?.inspectionDetails
                    ?.inspectionReportData?.documentInspection
                }
              />
            )}
        </div>
      )}
    </div>
  );
};

export default DetailInfoTable;